import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";
import "@/index.css";
import App from "@/App";

// Suppress benign ResizeObserver loop warning (triggered by Radix UI) so the
// CRA dev overlay doesn't block the UI.
const _roErr = /ResizeObserver loop/;
window.addEventListener("error", (e) => {
  if (_roErr.test(e.message)) {
    e.stopImmediatePropagation();
  }
});


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register the PWA service worker (production/HTTPS). Enables install + offline shell.
// Also detects new deployments and shows a "Nueva versión disponible" toast.
let updateToastShown = false;
let updateApplying = false;

async function applyUpdate() {
  try {
    const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : null;
    if (reg && reg.waiting) {
      updateApplying = true;
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
      // Safety fallback in case controllerchange doesn't fire.
      setTimeout(() => window.location.reload(), 2500);
      return;
    }
  } catch (e) {}
  window.location.reload();
}

function showUpdateToast() {
  if (updateToastShown) return;
  updateToastShown = true;
  toast("Nueva versión disponible", {
    description: "Actualiza para obtener las últimas mejoras.",
    duration: Infinity,
    action: { label: "Actualizar", onClick: () => applyUpdate() },
  });
}

// Detect redeploys by comparing CRA's asset-manifest.json (its hashes change
// every production build). No-ops in dev where the file isn't JSON.
async function fetchManifestSig() {
  try {
    const res = await fetch("/asset-manifest.json", { cache: "no-store" });
    if (!res.ok) return null;
    const j = await res.json();
    return JSON.stringify(j.entrypoints || j.files || j);
  } catch (e) {
    return null;
  }
}

function startVersionPolling() {
  let current = null;
  fetchManifestSig().then((sig) => { current = sig; });
  const check = async () => {
    if (updateToastShown) return;
    const sig = await fetchManifestSig();
    if (sig && current && sig !== current) showUpdateToast();
    if (sig && !current) current = sig;
  };
  setInterval(check, 60 * 1000);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) check(); });
}

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (updateApplying) window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").then((reg) => {
      if (reg.waiting && navigator.serviceWorker.controller) showUpdateToast();
      reg.addEventListener("updatefound", () => {
        const nw = reg.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed" && navigator.serviceWorker.controller) showUpdateToast();
        });
      });
    }).catch(() => {});
    startVersionPolling();
  });
}
